import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Box1 } from "iconsax-react";
import { EmptyState, Pagination } from "@/shared/components";
import { Prompt, PromptForm } from "@/shared/ui";
import { usePagination, useAuth, useBreadcrumbOverrides } from "@/shared/hooks";
import { TengeCircleIcon } from "@/shared/assets/icons";
import { formatMoneyKzt } from "@/features/accounting/shared";
import {
  useWarehouseListPage,
  useWarehouseModals,
  useWarehouseMutations,
  useWarehouseTableSort,
} from "../../hooks";
import type { AssignItemDto, InventoryUnit, BatchAssignRequest, BatchWriteOffRequest } from "../../types";
import { isConsumableUnit, aggregateInventoryUnits } from "../../utils";
import { WarehouseHeader, WarehouseSummaryCards, WarehousePageSkeleton, WarehouseFilters } from "../../components";
import type { SummaryCard } from "../../components/WarehouseSummaryCards";

const WarehouseTable = lazy(() => import("../../components/WarehouseTable").then((m) => ({ default: m.default })));
const WarehouseGroupsTable = lazy(() =>
  import("../../components/WarehouseGroupsTable").then((m) => ({ default: m.default })),
);
const AssetAssignModal = lazy(() => import("../../components/AssetAssignModal").then((m) => ({ default: m.default })));
const ItemDetailsModal = lazy(() => import("../../components/ItemDetailsModal").then((m) => ({ default: m.default })));
const TransferModal = lazy(() => import("../../components/TransferModal").then((m) => ({ default: m.default })));
const BulkTransferModal = lazy(() => import("../../components/BulkTransferModal").then((m) => ({ default: m.default })));
const RevalueModal = lazy(() => import("../../components/RevalueModal").then((m) => ({ default: m.default })));
const BatchDetailsModal = lazy(() => import("./components/BatchDetailsModal").then((m) => ({ default: m.default })));
const BatchAssignModal = lazy(() => import("./components/BatchAssignModal").then((m) => ({ default: m.default })));
const BatchWriteOffModal = lazy(() => import("./components/BatchWriteOffModal").then((m) => ({ default: m.default })));

export default function WarehousePage() {
  const { hasRole } = useAuth();
  const isChief = hasRole("chief");
  const location = useLocation();
  const { t: tPage } = useTranslation("WarehousePage");
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isError,
    filteredItems,
    groupedUnits,
    getGroupedUnits,
    sortConfig,
    handleSort,
    search,
    setSearch,
    assetTypeFilter,
    setAssetTypeFilter,
    statusFilter,
    setStatusFilter,
    subtypeFilter,
    setSubtypeFilter,
    vendorFilter,
    setVendorFilter,
    assetTypeOptions,
    statusOptions,
    subtypeOptions,
    vendorOptions,
    activeFilters,
    handleResetFilters,
    summary,
    t,
  } = useWarehouseListPage("inventory");

  useEffect(function resetOnNavigation() {
    handleResetFilters();
    setSelectedGroup(null);
  }, [location.key, handleResetFilters]);

  useBreadcrumbOverrides(useMemo((): Record<string, string> => {
    if (!selectedGroup) return {};
    return { warehouseGroupName: selectedGroup };
  }, [selectedGroup]));

  const summaryCards = useMemo<SummaryCard[]>(() => [
    { key: "totalUnits", label: tPage("summary.totalUnits"), value: summary.totalUnits.toString(), subtitle: tPage("summary.totalUnitsSubtitle"), icon: Box1 },
    { key: "totalValue", label: tPage("summary.totalValue"), value: formatMoneyKzt(summary.totalValue), subtitle: tPage("summary.totalValueSubtitle"), icon: TengeCircleIcon, isCustomIcon: true },
  ], [summary, tPage]);

  const {
    prompt,
    setPrompt,
    assignModal,
    setAssignModal,
    detailsModal,
    setDetailsModal,
    confirmWriteOff,
    setConfirmWriteOff,
    confirmReturn,
    setConfirmReturn,
    transferModal,
    setTransferModal,
    bulkTransferModal,
    setBulkTransferModal,
    revalueModal,
    setRevalueModal,
    batchDetailsModal,
    setBatchDetailsModal,
    batchAssignModal,
    setBatchAssignModal,
    confirmBatchWriteOff,
    setConfirmBatchWriteOff,
  } = useWarehouseModals();

  const {
    assigningId,
    writingOffId,
    batchAssigningId,
    batchWritingOffId,
    isAssigning,
    isWritingOff,
    isReturning,
    isTransferring,
    isBulkTransferring,
    isRevaluing,
    handleAssign,
    handleReturn,
    handleWriteOff,
    handleTransfer,
    handleBulkTransfer,
    handleRevalue,
    handleBatchAssign,
    handleBatchWriteOff,
  } = useWarehouseMutations(setPrompt);

  useEffect(() => {
    if (selectedGroup !== null && !activeFilters) {
      const groupExists = groupedUnits.some((g) => g.groupName === selectedGroup);
      if (!groupExists) {
        setSelectedGroup(null);
      }
    }
  }, [selectedGroup, groupedUnits, activeFilters]);

  const groupUnits = useMemo(() => {
    if (selectedGroup === null) return [];
    return getGroupedUnits(selectedGroup);
  }, [selectedGroup, getGroupedUnits]);

  const groupsPagination = usePagination(groupedUnits.length, 10, [
    search,
    assetTypeFilter,
    statusFilter,
    subtypeFilter,
    vendorFilter,
    selectedGroup,
  ]);
  const pageGroups = groupedUnits.slice(groupsPagination.startIndex, groupsPagination.endIndex);

  const tableRows = useMemo(() => aggregateInventoryUnits(groupUnits), [groupUnits]);
  const sortedTableRows = useWarehouseTableSort(tableRows, sortConfig);
  const unitsPagination = usePagination(sortedTableRows.length, 10, [selectedGroup, sortConfig]);
  const pageTableRows = sortedTableRows.slice(unitsPagination.startIndex, unitsPagination.endIndex);

  if (isLoading && !data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <WarehouseHeader selectedGroup={selectedGroup} onBack={handleBackToGroups} />
          <WarehousePageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <WarehouseHeader selectedGroup={selectedGroup} onBack={handleBackToGroups} />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
        </section>
      </>
    );
  }

  async function handleReturnConfirm(id: number) {
    await handleReturn(id);
    setConfirmReturn(null);
  }

  async function handleWriteOffConfirm(id: number) {
    await handleWriteOff(id, { reason: t("writeOff.defaultReason") });
    setConfirmWriteOff(null);
  }

  async function handleAssignConfirm(id: number, dto: AssignItemDto) {
    await handleAssign(id, dto);
  }

  async function handleBatchAssignConfirm(batchId: number, dto: BatchAssignRequest) {
    await handleBatchAssign(batchId, dto);
    setBatchAssignModal(null);
  }

  async function handleBatchWriteOffConfirm(batchId: number, dto: BatchWriteOffRequest) {
    await handleBatchWriteOff(batchId, dto);
    setConfirmBatchWriteOff(null);
  }

  function handleGroupSelect(groupName: string) {
    setSelectedGroup(groupName);
  }

  function handleBackToGroups() {
    handleResetFilters();
    setSelectedGroup(null);
  }

  function handleReturnClick(id: number) {
    const unit = Array.isArray(data) ? data.find((u) => u.id === id) : undefined;
    if (unit && isConsumableUnit(unit.asset_type, unit.inventory_subtype)) {
      setPrompt({
        title: t("messages.returnConsumableWarningTitle"),
        text: t("messages.returnConsumableWarningText"),
        variant: "warning",
      });
      return;
    }
    setConfirmReturn(id);
  }

  function handleTransferClick(unit: InventoryUnit) {
    if (isConsumableUnit(unit.asset_type, unit.inventory_subtype)) {
      setPrompt({
        title: t("messages.transferConsumableWarningTitle"),
        text: t("messages.transferConsumableWarningText"),
        variant: "warning",
      });
      return;
    }
    setTransferModal(unit);
  }

  function handleBulkTransferClick(fromEmployeeId: number, fromEmployeeName: string) {
    setBulkTransferModal({ fromEmployeeId, fromEmployeeName });
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <WarehouseHeader selectedGroup={selectedGroup} onBack={handleBackToGroups} />
        <WarehouseSummaryCards cards={summaryCards} />

        <div className="radius-3xl surface-base-fill p-7">
          {selectedGroup !== null && (
            <WarehouseFilters
              search={search}
              onSearchChange={setSearch}
              assetTypeFilter={assetTypeFilter}
              onAssetTypeChange={setAssetTypeFilter}
              assetTypeOptions={assetTypeOptions}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              statusOptions={statusOptions}
              subtypeFilter={subtypeFilter}
              onSubtypeChange={setSubtypeFilter}
              subtypeOptions={subtypeOptions}
              vendorFilter={vendorFilter}
              onVendorChange={setVendorFilter}
              vendorOptions={vendorOptions}
              hasActiveFilters={activeFilters}
              onReset={() => {
                handleResetFilters();
                setSelectedGroup(null);
              }}
            />
          )}

          {filteredItems.length === 0 ? (
            <EmptyState
              hasActiveFilters={activeFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : selectedGroup === null ? (
            <>
              <Suspense fallback={<WarehousePageSkeleton tableOnly />}>
                <WarehouseGroupsTable
                  groups={pageGroups}
                  onGroupSelect={handleGroupSelect}
                  onBulkTransfer={handleBulkTransferClick}
                  categoryLabel={t("categoryLabel")}
                />
              </Suspense>

              <Pagination
                currentPage={groupsPagination.page}
                totalPages={groupsPagination.totalPages}
                onPageChange={groupsPagination.setPage}
                fromItem={groupsPagination.fromItem}
                toItem={groupsPagination.toItem}
                total={groupedUnits.length}
              />
            </>
          ) : (
            <>
              <Suspense fallback={<WarehousePageSkeleton tableOnly />}>
                <WarehouseTable
                  rows={pageTableRows}
                  onOpen={(id) => setDetailsModal(id)}
                  onAssign={(id) =>
                    setAssignModal(Array.isArray(data) ? data.find((item) => item.id === id) || null : null)
                  }
                  onReturn={handleReturnClick}
                  onWriteOff={(id) => setConfirmWriteOff(id)}
                  onTransfer={handleTransferClick}
                  onRevalue={(item) => setRevalueModal(item)}
                  onBatchDetails={(row) => setBatchDetailsModal(row)}
                  onBatchAssign={(row) => setBatchAssignModal(row)}
                  onBatchWriteOff={(row) => setConfirmBatchWriteOff(row)}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  assigningId={assigningId}
                  writingOffId={writingOffId}
                  batchAssigningId={batchAssigningId}
                  batchWritingOffId={batchWritingOffId}
                  hideStatusColumn={selectedGroup !== t("groups.unassigned")}
                />
              </Suspense>

              <Pagination
                currentPage={unitsPagination.page}
                totalPages={unitsPagination.totalPages}
                onPageChange={unitsPagination.setPage}
                fromItem={unitsPagination.fromItem}
                toItem={unitsPagination.toItem}
                total={sortedTableRows.length}
              />
            </>
          )}
        </div>
      </section>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => setPrompt(null)}
          namespace="WarehousePage"
        />
      )}

      {confirmReturn !== null && (
        <PromptForm
          title={t("confirm.returnTitle")}
          text={t("confirm.returnText")}
          variant="warning"
          onClose={() => setConfirmReturn(null)}
          onConfirm={() => handleReturnConfirm(confirmReturn)}
          isLoading={isReturning}
          confirmText={t("confirm.returnConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="WarehousePage"
        />
      )}

      {confirmWriteOff !== null && (
        <PromptForm
          title={t("confirm.writeOffTitle")}
          text={t("confirm.writeOffText")}
          variant="error"
          onClose={() => setConfirmWriteOff(null)}
          onConfirm={() => handleWriteOffConfirm(confirmWriteOff)}
          isLoading={isWritingOff}
          confirmText={t("confirm.writeOffConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="WarehousePage"
        />
      )}

      {assignModal && (
        <Suspense fallback={null}>
          <AssetAssignModal
            item={assignModal}
            onClose={() => setAssignModal(null)}
            onConfirm={handleAssignConfirm}
            isLoading={isAssigning}
            isChief={isChief}
            hasBackground={detailsModal === null && batchDetailsModal === null}
          />
        </Suspense>
      )}

      {detailsModal !== null && (
        <Suspense fallback={null}>
          <ItemDetailsModal
            itemId={detailsModal}
            onClose={() => setDetailsModal(null)}
            onAssign={(id) => setAssignModal(Array.isArray(data) ? data.find((item) => item.id === id) || null : null)}
            onReturn={handleReturnClick}
            onWriteOff={(id) => setConfirmWriteOff(id)}
            onTransfer={handleTransferClick}
            onRevalue={(unit) => {
              setDetailsModal(null);
              setRevalueModal(unit);
            }}
            assigningId={assigningId}
            writingOffId={writingOffId}
            zIndex={batchDetailsModal ? 10100 : undefined}
            hasBackground={!batchDetailsModal}
          />
        </Suspense>
      )}

      {batchDetailsModal && (
        <Suspense fallback={null}>
          <BatchDetailsModal
            row={batchDetailsModal}
            onClose={() => setBatchDetailsModal(null)}
          />
        </Suspense>
      )}

      {batchAssignModal && (
        <Suspense fallback={null}>
          <BatchAssignModal
            row={batchAssignModal}
            onClose={() => setBatchAssignModal(null)}
            onConfirm={handleBatchAssignConfirm}
            isLoading={batchAssigningId !== null}
            isChief={isChief}
          />
        </Suspense>
      )}

      {confirmBatchWriteOff && (
        <Suspense fallback={null}>
          <BatchWriteOffModal
            row={confirmBatchWriteOff}
            onClose={() => setConfirmBatchWriteOff(null)}
            onConfirm={handleBatchWriteOffConfirm}
            isLoading={batchWritingOffId !== null}
          />
        </Suspense>
      )}

      {transferModal && (
        <Suspense fallback={null}>
          <TransferModal
            item={transferModal}
            onClose={() => setTransferModal(null)}
            onConfirm={handleTransfer}
            isLoading={isTransferring}
          />
        </Suspense>
      )}

      {bulkTransferModal && (
        <Suspense fallback={null}>
          <BulkTransferModal
            target={bulkTransferModal}
            onClose={() => setBulkTransferModal(null)}
            onConfirm={handleBulkTransfer}
            isLoading={isBulkTransferring}
          />
        </Suspense>
      )}

      {revalueModal && (
        <Suspense fallback={null}>
          <RevalueModal
            item={revalueModal}
            onClose={() => setRevalueModal(null)}
            onConfirm={handleRevalue}
            isLoading={isRevaluing}
          />
        </Suspense>
      )}
    </>
  );
}
